import React, { useEffect, useState } from 'react';
import { Download, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const DownloadPage = () => {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    // Inicia o download automaticamente após 2 segundos
    const timer = setTimeout(() => {
      handleDownload();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleDownload = () => {
    setDownloading(true);
    
    // Cria um link temporário para download
    const link = document.createElement('a');
    link.href = '/build.zip';
    link.download = 'mcengenharia-build.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Marca como baixado após 1 segundo
    setTimeout(() => {
      setDownloading(false);
      setDownloaded(true);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#073A40] to-[#06323B] flex items-center justify-center px-4">
      <Card className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 sm:p-12">
        <div className="text-center">
          {/* Logo ou Ícone */}
          <div className={`w-24 h-24 ${downloaded ? 'bg-green-100' : 'bg-[#073A40]'} rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300`}>
            {downloaded ? (
              <CheckCircle2 className="text-green-600" size={48} />
            ) : (
              <Download className={`text-white ${downloading ? 'animate-bounce' : ''}`} size={48} />
            )}
          </div>

          {/* Título */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#073A40] mb-4">
            {downloaded ? 'Download Concluído!' : downloading ? 'Baixando...' : 'Preparando Download'}
          </h1>

          {/* Descrição */}
          <p className="text-lg text-[#06323B] mb-8">
            {downloaded 
              ? 'O arquivo build.zip foi baixado com sucesso!' 
              : downloading 
              ? 'Aguarde enquanto o download é iniciado...'
              : 'O download iniciará automaticamente em instantes.'
            }
          </p>

          {/* Informações do Arquivo */}
          <div className="bg-[#F2F2F2] rounded-lg p-6 mb-8">
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              <div>
                <p className="text-sm text-[#A69076] font-semibold mb-1">Nome do Arquivo</p>
                <p className="text-base text-[#073A40]">mcengenharia-build.zip</p>
              </div>
              <div>
                <p className="text-sm text-[#A69076] font-semibold mb-1">Tamanho</p>
                <p className="text-base text-[#073A40]">~560 KB</p>
              </div>
              <div>
                <p className="text-sm text-[#A69076] font-semibold mb-1">Tipo</p>
                <p className="text-base text-[#073A40]">Build de Produção</p>
              </div>
              <div>
                <p className="text-sm text-[#A69076] font-semibold mb-1">Plataforma</p>
                <p className="text-base text-[#073A40]">Netlify Ready</p>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!downloaded && (
              <Button 
                onClick={handleDownload}
                disabled={downloading}
                size="lg"
                className="bg-[#073A40] hover:bg-[#06323B] text-white font-bold px-8"
              >
                <Download className="mr-2" size={20} />
                {downloading ? 'Baixando...' : 'Baixar Agora'}
              </Button>
            )}
            
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-[#073A40] text-[#073A40] hover:bg-[#073A40] hover:text-white"
            >
              <a href="/">
                Voltar ao Site
              </a>
            </Button>
          </div>

          {/* Instruções */}
          {downloaded && (
            <div className="mt-8 p-6 bg-green-50 rounded-lg border-2 border-green-200">
              <h3 className="text-lg font-bold text-green-800 mb-3">
                📝 Próximos Passos
              </h3>
              <ol className="text-left text-sm text-green-700 space-y-2">
                <li>1. Extraia o arquivo <code className="bg-green-100 px-2 py-1 rounded">build.zip</code></li>
                <li>2. Acesse <a href="https://app.netlify.com/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Netlify</a></li>
                <li>3. Arraste a pasta <code className="bg-green-100 px-2 py-1 rounded">build/</code> para o Netlify</li>
                <li>4. Aguarde o deploy completar</li>
                <li>5. Teste o site em produção!</li>
              </ol>
            </div>
          )}

          {/* Documentação */}
          <div className="mt-8 p-6 bg-[#F2F2F2] rounded-lg">
            <h3 className="text-lg font-bold text-[#073A40] mb-3">
              📚 Documentação Incluída
            </h3>
            <ul className="text-left text-sm text-[#06323B] space-y-1">
              <li>• DEPLOY_NETLIFY.md - Como fazer deploy</li>
              <li>• ROTEIRO_GOOGLE_ADS.md - Campanha completa</li>
              <li>• SITELINKS_GOOGLE_ADS.md - Configuração dos sitelinks</li>
              <li>• INSTRUCOES_GOOGLE_ADS_TRACKING.md - Tracking de conversões</li>
              <li>• COMO_GERAR_BUILD.md - Gerar build no futuro</li>
            </ul>
          </div>

          {/* Links Úteis */}
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
            <a 
              href="https://app.netlify.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#A69076] hover:text-[#073A40] flex items-center gap-1"
            >
              <ExternalLink size={14} />
              Acessar Netlify
            </a>
            <span className="text-[#A69F97]">•</span>
            <a 
              href="https://docs.netlify.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#A69076] hover:text-[#073A40] flex items-center gap-1"
            >
              <ExternalLink size={14} />
              Documentação Netlify
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DownloadPage;
